import React, { useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// -----------------------------------------------------------------------
// Waitlist Admin (2026-08-24)
//
// Read-only surface over the marketing site's ``db.waitlist`` collection.
// Roger uses this to see who joined the founding list, filter by product,
// and export a CSV for offline analysis.  A per-product breakdown is
// shown at the top so he can see momentum at a glance.
// -----------------------------------------------------------------------

const PRODUCTS = [
  { key: "MDD",         name: "MDD",              priority: true,  free: true  },
  { key: "Evident",     name: "Evident",          priority: true                },
  { key: "Mirror",      name: "Mirror",           priority: true                },
  { key: "Kindred",     name: "Kindred",          priority: true,  free: true  },
  { key: "TheWell",     name: "The Well",         free: true                    },
  { key: "MemIQ",       name: "MemIQ"                                            },
  { key: "Lighthouse",  name: "Lighthouse"                                       },
  { key: "LifeOS",      name: "LifeOS"                                           },
  { key: "Booknook",    name: "BookNookExchange"                                 },
  { key: "Hub",         name: "Hub (all)"                                        },
];

function fmtDate(v) {
  if (!v) return "\u2014";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return String(v);
  }
}

function TouchProgress({ founding, chapters, unsub }) {
  if (unsub) return <Pill tone="muted">Unsubscribed</Pill>;
  const stage = founding || 0;
  if (stage >= 4 && chapters > 0) {
    return <Pill tone="success">Chapter {chapters}</Pill>;
  }
  if (stage >= 4) return <Pill tone="success">Founding complete</Pill>;
  if (stage >= 1) {
    return (
      <Pill tone={stage >= 3 ? "success" : undefined}>
        Touch {stage} of 4
      </Pill>
    );
  }
  return <Pill tone="warn">Welcome pending</Pill>;
}

export default function WaitlistAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productFilter, setProductFilter] = useState(null);
  const [unsubFilter, setUnsubFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [audit, setAudit] = useState(null);

  // 2026-10-31 · homepage number vs raw rows, with exclusion reasons.
  // Owner-only endpoint; failure here never blocks the page.
  useEffect(() => {
    let alive = true;
    api
      .get("/waitlist/public-count-audit")
      .then((r) => { if (alive) setAudit(r.data); })
      .catch(() => { if (alive) setAudit(null); });
    return () => { alive = false; };
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (productFilter) params.set("product", productFilter);
      if (unsubFilter !== null) params.set("unsubscribed", String(unsubFilter));
      const r = await api.get(`/waitlist?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFilter, unsubFilter]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) => {
      return (
        (r.email || "").toLowerCase().includes(q) ||
        (r.name || "").toLowerCase().includes(q) ||
        (r.source || "").toLowerCase().includes(q)
      );
    });
  }, [data, search]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (productFilter) params.set("product", productFilter);
      if (unsubFilter !== null) params.set("unsubscribed", String(unsubFilter));
      const r = await api.get(`/waitlist/export.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `waitlist_${stamp}${productFilter ? "_" + productFilter : ""}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const summary = data?.summary || {};
  const byProduct = summary.by_product || [];
  const complianceHold = data?.compliance_hold || null;

  return (
    <Shell>
      <div className="bo-page" data-testid="waitlist-admin">
        <div style={{ marginBottom: 16 }}>
          <div className="bo-eyebrow">Marketing &middot; Waitlist</div>
          <h1
            style={{
              fontFamily: "var(--bo-serif)",
              fontSize: 28,
              margin: "4px 0 4px 0",
            }}
          >
            Founding waitlist
          </h1>
          <p
            style={{
              color: "var(--bo-muted)",
              margin: 0,
              fontSize: 14,
            }}
          >
            Everyone who joined the founding list from the marketing site.
            Filter by product, search by email, or export the full list.
          </p>
        </div>

        {complianceHold && complianceHold.notice ? (
          <div
            data-testid="waitlist-compliance-hold"
            style={{
              padding: "10px 14px",
              marginBottom: 12,
              borderRadius: 8,
              border: "1px solid rgba(196, 132, 17, 0.35)",
              background: "rgba(196, 132, 17, 0.08)",
              color: "#8a5a0b",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span aria-hidden style={{ fontSize: 16 }}>&#9888;</span>
            <span>
              <strong>{complianceHold.notice}</strong>
              {complianceHold.held_queue_size > 0 ? (
                <>
                  {" "}
                  <span style={{ opacity: 0.8 }}>
                    ({complianceHold.held_queue_size} subscriber
                    {complianceHold.held_queue_size === 1 ? "" : "s"} waiting on
                    the next tick)
                  </span>
                </>
              ) : null}
            </span>
          </div>
        ) : null}

        {/* 2026-10-30 · Touch-1 retry pass (owner-only count) */}
        {data?.welcome_retry ? (
          <div
            data-testid="waitlist-welcome-retry"
            style={{
              padding: "8px 14px",
              marginBottom: 12,
              borderRadius: 8,
              border: "1px solid var(--bo-line)",
              fontSize: 13,
              color: data.welcome_retry.exhausted > 0 ? "var(--bo-danger, #9b2c2c)" : "var(--bo-muted)",
            }}
          >
            Welcome (Touch 1) retry:{" "}
            <strong data-testid="waitlist-pending-welcomes">{data.welcome_retry.pending_welcomes}</strong> pending
            {data.welcome_retry.meta_lead_pending > 0 ? (
              <> ({data.welcome_retry.meta_lead_pending} Meta lead{data.welcome_retry.meta_lead_pending === 1 ? "" : "s"})</>
            ) : null}
            {" · "}
            <strong data-testid="waitlist-exhausted-welcomes">{data.welcome_retry.exhausted}</strong>{" "}
            with {data.welcome_retry.max_failures}+ failures
            {data.welcome_retry.exhausted > 0 ? " — see Incidents" : ""}
          </div>
        ) : null}

        {/* 2026-10-31 · Public homepage count audit (owner-only) */}
        {audit ? (
          <div
            data-testid="waitlist-public-count-audit"
            style={{
              padding: "8px 14px",
              marginBottom: 12,
              borderRadius: 8,
              border: "1px solid var(--bo-line)",
              fontSize: 13,
              color: "var(--bo-muted)",
            }}
          >
            Homepage count:{" "}
            <strong data-testid="waitlist-public-count">{Number(audit.public_count).toLocaleString("en-US")}</strong>{" "}
            unique real people · raw rows{" "}
            <strong data-testid="waitlist-raw-total">{Number(audit.raw_total).toLocaleString("en-US")}</strong>
            {audit.excluded_total > 0 ? (
              <>
                {" · excluded "}
                <strong data-testid="waitlist-excluded-total">{audit.excluded_total}</strong>
                {" ("}
                {Object.entries(audit.excluded)
                  .map(([reason, n]) => `${reason.replace(/_/g, " ")}: ${n}`)
                  .join(", ")}
                {")"}
              </>
            ) : null}
            {audit.by_product && Object.keys(audit.by_product).length > 0 ? (
              <div
                data-testid="waitlist-public-count-by-product"
                style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "4px 14px" }}
              >
                {Object.entries(audit.by_product).map(([product, v]) => (
                  <span key={product} data-testid={`waitlist-public-count-product-${product}`}>
                    {product}:{" "}
                    <strong>{Number(v.public).toLocaleString("en-US")}</strong>
                    <span style={{ opacity: 0.7 }}> / {Number(v.raw).toLocaleString("en-US")} raw</span>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Summary strip */}
        <div
          className="bo-card"
          style={{ padding: 16, marginBottom: 12 }}
          data-testid="waitlist-summary"
        >
          <div
            style={{
              display: "flex",
              gap: 24,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <SummaryStat label="Total" value={summary.total} />
              <SummaryStat label="Active" value={summary.active} tone="success" />
              <SummaryStat
                label="Unsubscribed"
                value={summary.unsubscribed}
                tone="muted"
              />
            </div>
            <button
              className="bo-btn bo-btn--primary"
              onClick={downloadCsv}
              disabled={downloading || !data?.rows?.length}
              data-testid="waitlist-export-csv"
              style={{ padding: "10px 18px" }}
            >
              {downloading ? "Preparing\u2026" : "\u2193 Export CSV"}
            </button>
          </div>
        </div>

        {/* Per-product breakdown */}
        {byProduct.length > 0 && (
          <div
            className="bo-card"
            style={{ padding: 16, marginBottom: 12 }}
            data-testid="waitlist-by-product"
          >
            <div
              className="bo-eyebrow"
              style={{ marginBottom: 10, fontSize: 11 }}
            >
              By product
            </div>
            <div
              style={{
                display: "grid",
                gap: 10,
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(180px, 1fr))",
              }}
            >
              {byProduct.map((row) => {
                const meta = PRODUCTS.find((p) => p.key === row.product);
                const label = meta ? meta.name : row.product;
                const active = productFilter === row.product;
                return (
                  <button
                    key={row.product}
                    onClick={() =>
                      setProductFilter(active ? null : row.product)
                    }
                    data-testid={`waitlist-product-tile-${row.product}`}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      border: `1.5px solid ${
                        active
                          ? "var(--bo-accent, #234a8b)"
                          : "var(--bo-border, #e6e2d8)"
                      }`,
                      background: active
                        ? "var(--bo-accent-tint, #eef4ff)"
                        : "var(--bo-panel, #fff)",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {label}
                      {meta?.priority && (
                        <Pill tone="warn">Priority</Pill>
                      )}
                      {meta?.free && (
                        <Pill tone="success">Free</Pill>
                      )}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--bo-serif)",
                        fontSize: 22,
                        marginTop: 4,
                        fontWeight: 600,
                      }}
                    >
                      {row.active}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--bo-muted)",
                      }}
                    >
                      {row.total} joined
                      {row.unsubscribed > 0
                        ? ` \u00b7 ${row.unsubscribed} unsub`
                        : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div
          className="bo-card"
          style={{ padding: 12, marginBottom: 12 }}
          data-testid="waitlist-filters"
        >
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              className="bo-input"
              placeholder="Search email, name, or source\u2026"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 220, padding: "8px 12px" }}
              data-testid="waitlist-search"
            />
            <select
              className="bo-input"
              value={productFilter || ""}
              onChange={(e) => setProductFilter(e.target.value || null)}
              style={{ padding: "8px 12px" }}
              data-testid="waitlist-product-select"
            >
              <option value="">All products</option>
              {PRODUCTS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name}
                </option>
              ))}
            </select>
            <select
              className="bo-input"
              value={unsubFilter === null ? "" : String(unsubFilter)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") setUnsubFilter(null);
                else setUnsubFilter(v === "true");
              }}
              style={{ padding: "8px 12px" }}
              data-testid="waitlist-status-select"
            >
              <option value="">All statuses</option>
              <option value="false">Active only</option>
              <option value="true">Unsubscribed only</option>
            </select>
            <button
              className="bo-btn bo-btn--ghost"
              onClick={load}
              disabled={loading}
              data-testid="waitlist-refresh"
              style={{ padding: "8px 14px" }}
            >
              {loading ? "Loading\u2026" : "Refresh"}
            </button>
          </div>
        </div>

        {error && <ErrorBanner error={error} testId="waitlist-error" />}
        {loading && <LoadingCard />}
        {!loading && filteredRows.length === 0 && (
          <EmptyState
            title="No subscribers yet"
            description={
              productFilter
                ? `No one on the ${productFilter} list matches the current filter.`
                : "As soon as someone joins the founding waitlist, they'll appear here."
            }
            testId="waitlist-empty"
          />
        )}

        {!loading && filteredRows.length > 0 && (
          <div
            className="bo-card"
            style={{ padding: 0, overflow: "auto" }}
            data-testid="waitlist-table"
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: 860,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "var(--bo-panel-alt, #f6f4ef)",
                    fontSize: 11,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--bo-muted)",
                  }}
                >
                  <Th>Email</Th>
                  <Th>Name</Th>
                  <Th>Product</Th>
                  <Th>Joined</Th>
                  <Th>Sequence</Th>
                  <Th>Next send</Th>
                  <Th>Source</Th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr
                    key={r.id}
                    style={{
                      borderTop: "1px solid var(--bo-border, #e6e2d8)",
                    }}
                    data-testid={`waitlist-row-${r.id}`}
                  >
                    <Td>
                      <a
                        href={`mailto:${r.email}`}
                        style={{
                          color: "var(--bo-accent, #234a8b)",
                          textDecoration: "none",
                        }}
                      >
                        {r.email}
                      </a>
                    </Td>
                    <Td>
                      {r.name || (
                        <span style={{ color: "var(--bo-muted)" }}>—</span>
                      )}
                    </Td>
                    <Td>
                      <Pill tone="muted">{r.product}</Pill>
                    </Td>
                    <Td>
                      <span style={{ fontSize: 12 }}>{fmtDate(r.created_at)}</span>
                    </Td>
                    <Td>
                      <TouchProgress
                        founding={r.founding_touches_sent}
                        chapters={r.chapters_sent}
                        unsub={r.unsubscribed}
                      />
                    </Td>
                    <Td>
                      <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                        {r.unsubscribed ? "—" : fmtDate(r.next_send_at)}
                      </span>
                    </Td>
                    <Td>
                      <span
                        style={{
                          fontSize: 11,
                          color: "var(--bo-muted)",
                          fontFamily: "var(--bo-mono, ui-monospace)",
                          maxWidth: 220,
                          display: "inline-block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={r.source || ""}
                      >
                        {r.source || "—"}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}

function Th({ children }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "10px 14px",
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td style={{ padding: "10px 14px", verticalAlign: "middle" }}>
      {children}
    </td>
  );
}

function SummaryStat({ label, value, tone }) {
  const color =
    tone === "success"
      ? "#1f6c3f"
      : tone === "muted"
      ? "var(--bo-muted)"
      : "var(--bo-ink)";
  return (
    <div>
      <div
        className="bo-eyebrow"
        style={{ fontSize: 11, color: "var(--bo-muted)" }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--bo-serif)",
          fontSize: 24,
          color,
          fontWeight: 600,
        }}
        data-testid={`waitlist-summary-${label.toLowerCase()}`}
      >
        {value ?? 0}
      </div>
    </div>
  );
}
